import { getAuth } from "firebase/auth";
import { useAuthStore } from "@/stores/authStore";

const API_BASE = getApiBaseUrl();

export function getApiBaseUrl() {
  const basePort = 8000;
  const fallback = import.meta.env.VITE_API_BASE_URL;
  const host = window.location.hostname;
  const isProduction = import.meta.env.MODE === "production";

  if (isProduction) {
    return fallback;
  }

  if (host && host !== "localhost" && host !== "127.0.0.1") {
    return `http://${host}:${basePort}/api`;
  }

  return fallback;
}

// 🔹 Firebase SDK から ID トークンを取得（IndexedDB ベース）
export async function getAuthToken() {
  const auth = getAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("Firebase user is not logged in");
  }
  return await currentUser.getIdToken();
}

// 🔹 共通のAPIリクエスト関数
export async function fetchWithAuth(endpoint, options = {}) {
  let idToken;
  try {
    idToken = await getAuthToken();
  } catch {
    throw new Error("❌ Firebase Token is not available — user not logged in");
  }

  const authStore = useAuthStore();

  const headers = {
    Authorization: `Bearer ${idToken}`,
    "Content-Type": "application/json",
    Accept: "application/json",
    ...options.headers,
  };

  const method = (options.method || "GET").toUpperCase();

  // ✅ POST または PUT のときに is_guest を追加
  let body = options.body;
  if ((method === "POST" || method === "PUT") && authStore?.isGuest && body) {
    try {
      const parsed = JSON.parse(body);
      parsed.is_guest = true;
      body = JSON.stringify(parsed);
    } catch (e) {
      console.warn("⚠️ bodyがJSONでないため is_guest を追加できませんでした");
    }
  }

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      body,
      credentials: "include",
    });

    // 正常なレスポンス
    if (response.ok) {
      if (response.status === 204) {
        return null;
      }
      return await response.json();
    }

    // 401エラーの場合、トークンを強制リフレッシュして再試行
    if (response.status === 401) {
      console.warn("🔄 Token expired, attempting to refresh...");

      try {
        const auth = getAuth();
        await auth.currentUser.getIdToken(true); // 強制リフレッシュ

        // 再試行
        return await fetchWithAuth(endpoint, options);
      } catch (refreshError) {
        console.error("❌ Failed to refresh token", refreshError);
        return null;
      }
    }

    if (response.status === 204) {
      return null;
    }

    // その他のエラー処理
    const errorData = await response.json();
    console.error(`❌ API Error: ${response.status}`, errorData);
    // status を呼び出し元が判別できるよう、レスポンス本体に付与して投げる
    // （従来はここで status 情報が失われ、呼び出し元は文字列メッセージでしか判定できなかった）
    if (errorData && typeof errorData === "object") {
      errorData.status = response.status;
      throw errorData;
    }
    throw { status: response.status, error: errorData };
  } catch (error) {
    console.error("❌ API Request failed:", error);
    throw error;
  }
}

export async function fetchWithoutAuth(endpoint, options = {}) {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...options.headers,
  };

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      credentials: "include",
    });

    if (response.ok) {
      if (response.status === 204) {
        return null;
      }
      return await response.json();
    }

    if (response.status === 204) {
      return null;
    }

    const errorData = await response.json();
    console.error(`❌ Public API Error: ${response.status}`, errorData);
    throw new Error(`Public API Request failed: ${response.status}`);
  } catch (error) {
    console.error("❌ Public API Request failed:", error);
    return null;
  }
}
